import { execEventHandlerArgs } from 'brace';
import { GA_FIELD_PROPERTY, GA_FIELD_CATEGORY, EDITOR_COMMANDS } from './constants';
import {
  FieldPropName,
  FieldType,
  FieldValues,
  GAFieldsResponse,
  GAResource,
  QuerySelectedValues,
  SelectedFields,
} from './types';

/*
  Transform the fields coming from the API to a more suitable form
*/
export const transformFields = (
  allFields?: GAFieldsResponse | null,
  selectedFields?: SelectedFields | string[],
): FieldValues => {
  const returnValue = {
    [GA_FIELD_PROPERTY.SEGMENTS]: [] as FieldType[],
    [GA_FIELD_PROPERTY.ATTRIBUTES]: [] as FieldType[],
    [GA_FIELD_PROPERTY.METRICS]: [] as FieldType[],
  } as FieldValues;

  if (!allFields) {
    return returnValue;
  }
  let disabledFields: { [i: string]: string[] };
  let allSelectedFields: string[] = [];
  if (selectedFields) {
    if (Array.isArray(selectedFields)) {
      allSelectedFields = selectedFields;
    } else {
      allSelectedFields = [
        ...selectedFields[GA_FIELD_PROPERTY.ATTRIBUTES],
        ...selectedFields[GA_FIELD_PROPERTY.METRICS],
        ...selectedFields[GA_FIELD_PROPERTY.SEGMENTS],
      ];
    }
    disabledFields = getDisabledFields(allSelectedFields, allFields);
  }
  return Object.keys(allFields.fields).reduce((occum: FieldValues, key) => {
    const currentField = allFields.fields[key].field_details;

    const option: FieldType = {
      value: key,
      label: key,
      description: currentField.description,
      disabled: false,
      selected: false,
      category: currentField.category,
      incompatibleFields: [],
    };
    if (disabledFields) {
      option.incompatibleFields = getIncompatibleFields(key, disabledFields);
      option.disabled = option.incompatibleFields.length > 0;
    }
    if (allSelectedFields) {
      option.selected = allSelectedFields.includes(key);
    }
    switch (currentField.category) {
      case GA_FIELD_CATEGORY.SEGMENT:
        occum[GA_FIELD_PROPERTY.SEGMENTS].push(option);
        break;
      case GA_FIELD_CATEGORY.ATTRIBUTE:
        occum[GA_FIELD_PROPERTY.ATTRIBUTES].push(option);
        break;
      case GA_FIELD_CATEGORY.METRIC:
        occum[GA_FIELD_PROPERTY.METRICS].push(option);
        break;
    }
    return occum;
  }, returnValue);
};

/*
 Return all the incompatible according to the selected fields
 example: { selected_field1: ["inc_field2", "inc_fields3"] }
*/
const getDisabledFields = (selectedFields: string[], allFields: GAFieldsResponse) =>
  selectedFields.reduce((occum: Record<string, string[]>, field: string) => {
    const currentField = allFields.fields[field];
    occum[field] = currentField?.incompatible_fields || [];
    return occum;
  }, {});

/**
  Returns the resource from a query string ie "SELECT fields from resource" if exists
  if the value is empty means that no resource found
*/
export const getResourceFromQuery = (query = '') => {
  const resource = / from ([._a-z-]*) ?(?=where|$|)/i.exec(query);
  if (resource && resource[1]) {
    return resource[1].trim();
  }
  return '';
};
/*
 Parses a query string ("SELECT field1,field2 from resource")
 and returns the selected values as an object
*/
export const parseQuery = (
  query?: string,
  allFields?: GAFieldsResponse | null,
): QuerySelectedValues => {
  const defaultValue = {
    from: getResourceFromQuery(query),
    select: {
      [GA_FIELD_PROPERTY.SEGMENTS]: [] as string[],
      [GA_FIELD_PROPERTY.ATTRIBUTES]: [] as string[],
      [GA_FIELD_PROPERTY.METRICS]: [] as string[],
    } as SelectedFields,
  };

  if (!allFields || !query) {
    return defaultValue;
  }
  const selectFields = /select(.*)from/i.exec(query);
  if (selectFields) {
    const fields = selectFields[1].split(/,| /).map((f) => f.replace(',', '').trim());
    fields.forEach((field: string) => {
      if (allFields[GA_FIELD_PROPERTY.SEGMENTS].includes(field)) {
        defaultValue.select[GA_FIELD_PROPERTY.SEGMENTS].push(field);
      } else if (allFields[GA_FIELD_PROPERTY.METRICS].includes(field)) {
        defaultValue.select[GA_FIELD_PROPERTY.METRICS].push(field);
      } else if (allFields[GA_FIELD_PROPERTY.ATTRIBUTES].includes(field)) {
        defaultValue.select[GA_FIELD_PROPERTY.ATTRIBUTES].push(field);
      }
    });
  }
  return defaultValue;
};

/*
  Parses the Selected values and return a query string
  inputQuery: Because we only support select, from,
   we need to add everything else that might exist after from in an existing query
*/
export const parseSelectedValues = (selectedValues: QuerySelectedValues, inputQuery?: string) => {
  const { select, from } = selectedValues;
  let query = `SELECT ... FROM ${from}`;
  const selectFields = [
    ...select[GA_FIELD_PROPERTY.ATTRIBUTES],
    ...select[GA_FIELD_PROPERTY.METRICS],
    ...select[GA_FIELD_PROPERTY.SEGMENTS],
  ];

  if (selectFields.length > 0) {
    query = `SELECT ${selectFields.join(', ')} FROM ${from}`;
  }

  if (inputQuery) {
    const otherStatements = inputQuery.split(` ${from} `)[1] || '';
    query = `${query} ${otherStatements}`;
  }
  return query;
};

/*
  Returns an array with the incompatible fields for a specific field
  this is used to display what selected fields are disabling the specified one
*/
const getIncompatibleFields = (field: string, disabledFields: Record<string, string[]>): string[] =>
  Object.keys(disabledFields).reduce((occum: string[], fieldName) => {
    if (disabledFields[fieldName].includes(field)) {
      occum.push(fieldName);
    }
    return occum;
  }, []);

/*
 Removes any selected field that dont't exist in the original fields
  response coming from API for a specific resource
*/
export const filterNonExistingFields = (
  selectedFields: SelectedFields,
  allFields: GAFieldsResponse,
): SelectedFields => {
  const returnValue = {
    [GA_FIELD_PROPERTY.SEGMENTS]: [] as string[],
    [GA_FIELD_PROPERTY.ATTRIBUTES]: [] as string[],
    [GA_FIELD_PROPERTY.METRICS]: [] as string[],
  } as SelectedFields;

  return Object.keys(selectedFields).reduce((occum, key: string) => {
    occum[key as FieldPropName] = selectedFields[key as FieldPropName].filter((f) =>
      allFields[key as FieldPropName].includes(f),
    );
    return occum;
  }, returnValue);
};

/*
 Returns autocompletion according to the cursor position
 Between select and from returns the that available fields that are not selected and not disabled
 Between from and where return available resources
 After where returns all the fields
*/
export const getAutosuggestion = (
  val: string,
  cursorIndex: number,
  resources: GAResource[],
  fields: FieldValues,
) => {
  const value = val.toLowerCase();
  const fromPosition = value.indexOf(' from ');
  const selectPosition = value.indexOf('select ');
  const wherePosition = value.indexOf(' where ');
  const isBetweenSelectAndFrom = cursorIndex <= fromPosition && cursorIndex >= selectPosition;
  const isBetweenFromAndWhere =
    cursorIndex > fromPosition + 6 && (cursorIndex <= wherePosition || wherePosition < 0);
  let autoCompleteValues: FieldType[] = [];
  if (isBetweenFromAndWhere) {
    autoCompleteValues = resources.map((r) => ({
      value: r.name,
      label: r.displayName,
      description: r.description,
      category: 'RESOURCE',
    }));
  } else {
    autoCompleteValues = [
      ...fields[GA_FIELD_PROPERTY.ATTRIBUTES],
      ...fields[GA_FIELD_PROPERTY.SEGMENTS],
      ...fields[GA_FIELD_PROPERTY.METRICS],
    ];
    if (isBetweenSelectAndFrom) {
      // remove selected and disabled fields from auto-completion list
      // when selecting columns
      autoCompleteValues = autoCompleteValues.filter((f) => !f.disabled && !f.selected);
    }
  }
  return autoCompleteValues;
};
// Prevent SELECT FROM keywords deletion from the editor
export const preventStatementCommandDeletion = (
  e: Pick<execEventHandlerArgs, 'command' | 'editor' | 'stopPropagation' | 'preventDefault'>,
) => {
  if (
    [EDITOR_COMMANDS.BACKSPACE, EDITOR_COMMANDS.INSERT, EDITOR_COMMANDS.DEL].includes(
      e.command.name,
    )
  ) {
    const currentValue = e.editor.getValue().toLowerCase();
    const fromPosition = currentValue.indexOf(' from ');
    const selectPosition = currentValue.indexOf('select ');
    const cursorIndex = e.editor.session.doc.positionToIndex(e.editor.selection.getCursor(), 0) - 1;
    let isDeletingSelect = false;
    let isDeletingFrom = false;
    if (e.command.name === EDITOR_COMMANDS.INSERT) {
      isDeletingSelect = cursorIndex + 2 > selectPosition && cursorIndex < selectPosition + 6;
      isDeletingFrom = cursorIndex + 1 > fromPosition && cursorIndex < fromPosition + 5;
    } else if ([EDITOR_COMMANDS.BACKSPACE, EDITOR_COMMANDS.DEL].includes(e.command.name)) {
      isDeletingSelect = cursorIndex + 1 > selectPosition && cursorIndex - 1 < selectPosition + 6;
      isDeletingFrom = cursorIndex + 1 > fromPosition && cursorIndex - 1 < fromPosition + 5;
    }
    if (isDeletingSelect || isDeletingFrom) {
      e.stopPropagation();
      e.preventDefault();
    }
  }
};
